import React, { useEffect, useState } from "react";

import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useTimezone } from "hooks/useTimezone";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { DweenTimeUnit, DweenTimeUnitEnum } from "utils/enums";
import { addToDate, truncateDate } from "utils/time-utils";
import { MeasurementDeviceTableAgregProps } from "./MeasurementDeviceTableAgregTypes";
import "./Table.css";

const defaultPrecision = 2;
const defaultScaling = 1.0;
const defaultEntriesToShow = 10;

export default function MeasurementDeviceTableAgreg(props: MeasurementDeviceTableAgregProps) {
  // TODO for every request check response code, must be 200(ok or 201...)
  const tz = useTimezone();

  const { measurement_device_tags } = props;
  const { columns } = props;

  const today = new Date();

  const [date, setDate] = useState(new Date());
  const [period, setPeriod] = useState<DweenTimeUnit>(DweenTimeUnitEnum.DAYS);
  const showPeriod = [
    { name: "1D", value: DweenTimeUnitEnum.DAYS },
    { name: "1W", value: DweenTimeUnitEnum.WEEKS },
    { name: "1M", value: DweenTimeUnitEnum.MONTHS },
  ];
  const [dateFrom, setDateFrom] = useState<number | null>(null);
  const [dateTo, setDateTo] = useState<number | null>(null);

  const urlEasas = props.customHost
    ? props.customHost
    : `${window.location.origin.toString() + window.contextRoot}`;

  const addressField = "address";

  const [tableColumns, setTableColumns] = useState([]);

  const measuremenetDevicesEndpoint = "/rest/measurementDevices/devicesByTags";
  const [measurementDevices, setMeasurementDevices] = useState([]);
  const [filteredMeasurementDevicesLoaded, setFilteredMeasurementDevicesLoaded] = useState(false);
  const [filteredMeasurementDevices, setFilteredMeasurementDevices] = useState([]);

  const datasetsAggregatedEndpoint = "/rest/datasets/data/intervalAggregated";

  const [entries, setEntries] = useState([]);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: props.entriesToShow ? props.entriesToShow : defaultEntriesToShow, // TODO may produce error
    page: 1,
    filters: {
      address: { value: "", matchMode: "contains" },
    },
  });
  const [filterHasChanged, setFilterHasChanged] = useState(true);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
  }, [dateFrom, dateTo, lazyParams, tableColumns, period]);

  // https://usehooks.com/useLocalStorage/ -- reikia padaryti globalu cache - laikyti session storage, ar kur nors kitur
  // Loads dynamic columns
  useEffect(() => {
    setPeriod("DAYS");
    setDate(new Date());
    const colArr = [];
    colArr.push({
      field: addressField,
      header: "Adresas",
      unit: "",
      aggregation_type: undefined,
      doFilter: true,
    });
    for (const [key, value] of Object.entries(columns)) {
      colArr.push({
        field: key,
        header: value.header,
        unit: value.unit,
        aggregation_type: value.aggregation_type,
        doFilter: false,
      });
    }
    setTableColumns(colArr);
  }, []);

  // Loads measurement Devices
  useEffect(() => {
    if (measurementDevices.length === 0) {
      const tempUrl = new URL(urlEasas + measuremenetDevicesEndpoint);
      measurement_device_tags.forEach(value => {
        tempUrl.searchParams.append("tagObjectIds", value);
      });
      tempUrl.searchParams.append("includeDatasets", true);

      let requestInit = {};
      if (props.token) {
        var header = new Headers({
          Authorization: `Bearer ${props.token}`,
        });
      } else {
        var header = new Headers({});
      }
      if (props.customHost) {
        requestInit = {
          headers: header, // TODO remove from production
        };
      } else {
        requestInit = {
          credentials: "same-origin", // TODO uncommnet for production
          headers: header, // TODO remove from production
        };
      }
      fetch(tempUrl, requestInit)
        .then(response => {
          if (!response.ok) {
            throw `Bad response status code:${response.status}`;
          }
          return response.json();
        })
        .then(data => {
          setMeasurementDevices(data);
        })
        .catch(function (err) {
          console.log(`Error while Fetching data from server, error:${err.message}`);
          setLoading(false);
          console.log("Error, Failed to request measurement devices");
        });
    }
  }, [measurementDevices]);

  // Truncates date for request
  useEffect(() => {
    if (!date) return;
    const start = truncateDate(date, period, tz.company);
    const end = addToDate(start, 1, period);

    if (dateFrom !== start.getTime() || dateTo !== end.getTime()) {
      insertLoadingData();
      setDateFrom(start.getTime());
      setDateTo(end.getTime());
    }
  }, [date, period]);

  // Filters measurement devices
  useEffect(() => {
    if (measurementDevices.length !== 0) {
      if (filterHasChanged) {
        const matchString = lazyParams.filters.address.value;
        const filteredMeaDevicesList = filterMeasurementDevices(measurementDevices, matchString);
        setFilteredMeasurementDevices(filteredMeaDevicesList);
        setFilterHasChanged(false);
      }
      setFilteredMeasurementDevicesLoaded(true);
    }
  }, [measurementDevices, lazyParams]);

  function setSafeValue(value, field, defaultValue) {
    if (value[field] === undefined) {
      return defaultValue;
    }
    return value[field];
  }

  // Loads table map
  const filterMeasurementDevices = (mDevices, criteria) => {
    const tableList = [];

    mDevices.forEach((object, index) => {
      const deviceId = object.meterId;
      criteria = criteria || "";

      if (deviceId.toLowerCase().includes(criteria.toLowerCase()) || criteria === "") {
        // filter condition
        const cells = [];
        for (const [key, value] of Object.entries(columns)) {
          let id = -1;

          // eslint-disable-next-line no-loop-func
          object.deviceDatasets.forEach(dataSet => {
            if (dataSet.hasOwnProperty("mappedName")) {
              if (value.parameter.localeCompare(dataSet.mappedName) === 0) {
                id = dataSet.categorizedDatasetId;
              }
            }
          });

          const precision = setSafeValue(value, "precision", defaultPrecision);
          const scaling = setSafeValue(value, "scaling", defaultScaling);
          const cell = {
            header: key,
            aggType: value.aggregation_type,
            unit: value.unit,
            categorizedDatasetId: id,
            precision,
            scaling,
          };
          cells.push(cell);
        }

        const mappedEntrie = {
          address: object.title,
          values: cells,
        };
        tableList.push(mappedEntrie);
      }
    });
    return tableList;
  };

  // Loads lazy data
  useEffect(() => {
    if (filteredMeasurementDevicesLoaded && !filterHasChanged) {
      setLoading(true);
      setFetchedAggregatedData([]);
      prepareReqBodyForAggregatedDateset();
    }
  }, [lazyParams, dateFrom, dateTo, filteredMeasurementDevicesLoaded, filterHasChanged]);

  // Prepares request body to request values
  const prepareReqBodyForAggregatedDateset = () => {
    let makeNewRequest = false;
    for (let i = 0; i < filteredMeasurementDevices.length; i++) {
      if (entries[i] === undefined || entries[i][Object.keys(entries[i])[1]].type === "i") {
        makeNewRequest = true;
        insertLoadingData();
        break;
      }
    }

    if (makeNewRequest) {
      for (const [index, [key, value]] of Object.entries(Object.entries(columns))) {
        const bodyMap = new Map();
        for (let i = 0; i < filteredMeasurementDevices.length; i++) {
          const element = filteredMeasurementDevices[i];

          const val = element.values[index];

          if (val.categorizedDatasetId !== -1) {
            bodyMap.set(val.categorizedDatasetId, val.aggType);
          }
        }
        // eslint-disable-next-line no-loop-func
        RequestDatasetAggregated(bodyMap, key, value);
      }
    } else {
      setLoading(false);
    }
  };

  const [fetchedAggregatedData, setFetchedAggregatedData] = useState([]);

  // Sends request to
  const RequestDatasetAggregated = (bodyMap, key, value) => {
    if (filteredMeasurementDevicesLoaded) {
      const tempUrl = new URL(urlEasas + datasetsAggregatedEndpoint);
      tempUrl.searchParams.append("from", dateFrom);
      tempUrl.searchParams.append("to", dateTo);
      tempUrl.searchParams.append("aggregationPeriod", period);

      const body = JSON.stringify(Object.fromEntries(bodyMap));

      let header;
      let requestInit;
      if (props.token) {
        header = new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${props.token}`,
        });
      } else {
        header = new Headers({
          "Content-Type": "application/json",
        });
      }

      if (props.customHost) {
        requestInit = {
          method: "POST",
          headers: header,
          body,
        };
      } else {
        requestInit = {
          method: "POST",
          headers: header,
          body,
          credentials: "same-origin",
        };
      }

      fetch(tempUrl, requestInit)
        .then(response => {
          if (!response.ok) {
            throw `Bad response status code:${response.status}`;
          }
          return response.json();
        })
        .then(data => {
          setFetchedAggregatedData(fetchedAggregatedData => [...fetchedAggregatedData, { key, ...data }]);
        })
        .catch(function (err) {
          console.log(`Error while Fetching data from server, error:${err.message}`);
          setLoading(false);
          setFetchedAggregatedData(fetchedAggregatedData => [...fetchedAggregatedData, { key }]);
        });
    } else {
      easasAlert({
        summary: "Error",
        detail: "Measurement devices not loaded yet",
        severity: "error",
        life: 3000,
      });
      setLoading(false);
    }
  };

  const insertLoadingData = () => {
    setEntries([]);
    const entriesList = [];

    for (let i = 0; i < filteredMeasurementDevices.length; i++) {
      const data = filteredMeasurementDevices[i];
      const obj = { address: data.address };
      // eslint-disable-next-line no-loop-func
      data.values.forEach((val, j) => {
        // eina per mapo collumnus
        obj[val.header] = <i className="pi pi-spin pi-spinner" style={{ fontSize: "1em" }} />;
      });
      entriesList.push(obj);
    }

    const oldEntries = [];
    for (let i = 0, j = 0; i < filteredMeasurementDevices.length; i++, j++) {
      oldEntries[i] = entriesList[j];
    }
    setEntries(oldEntries);
  };

  useEffect(() => {
    if (fetchedAggregatedData.length === Object.keys(columns).length) {
      const merged = {};
      fetchedAggregatedData.forEach(element => {
        const colKey = element.key;
        for (const [key, value] of Object.entries(element)) {
          if (key !== "key") {
            merged[`${colKey}:${key}`] = value;
          }
        }
      });
      setFetchedAggregatedData([]);
      insertData(merged); // cia merged turi header kazkoki
    }
  }, [fetchedAggregatedData, filteredMeasurementDevicesLoaded]);

  const insertData = datasetsAggregatedTemp => {
    const tempLazyParams = lazyParams;
    const entriesList = [];

    for (let i = 0; i < filteredMeasurementDevices.length; i++) {
      const data = filteredMeasurementDevices[i];
      const obj = { address: data.address };
      // eslint-disable-next-line no-loop-func
      data.values.forEach((val, j) => {
        // eina per mapo collumnus
        const { header } = val; // if val is object.property, might not work
        // const header = val.replace('.', '').replace(' ', '')
        if (val.categorizedDatasetId !== -1) {
          try {
            const key = `${header}:${val.categorizedDatasetId}`;
            const _value = datasetsAggregatedTemp[key][dateFrom];
            if (_value != null) {
              const scaledValue = _value * val.scaling;
              const precisedValue = scaledValue.toFixed(val.precision);
              obj[header] = `${precisedValue} ${val.unit}`;
            } else {
              obj[header] = "-";
            }
          } catch (error) {
            console.log("Error while mapping data to datatable");
            obj[header] = "-";
          }
        } else {
          obj[header] = "-";
        }
      });
      entriesList.push(obj);
    }

    const oldEntries = [...entries];

    for (let i = 0, j = 0; i < filteredMeasurementDevices.length; i++, j++) {
      oldEntries[i] = entriesList[j];
    }

    setEntries(oldEntries);
    setLoading(false);
  };

  const dynamicColumns = tableColumns.map((col, i) => {
    if (col.doFilter) {
      return (
        <Column
          key={col.field}
          field={col.field}
          header={col.header}
          style={{ minWidth: "8rem" }}
          filter
          showFilterMenu={false}
          showFilterMatchModes={false}
        />
      );
    }
    return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: "8rem" }} />;
  });

  // DatasetSetpointRow -----------------------------------------------------------------------
  const onPage = event => {
    if (!loading) {
      const _lazyParams = { ...lazyParams, ...event };
      setLazyParams(_lazyParams);
    }
  };

  const onSort = event => {
    const _lazyParams = { ...lazyParams, ...event };
    setLazyParams(_lazyParams);
  };

  const onFilter = event => {
    const searchValue = event.filters.address.value;
    if (lazyParams.filters.address.value !== searchValue) {
      setFilterHasChanged(true);
    }
    setFilteredMeasurementDevicesLoaded(false);
    const _lazyParams = { ...lazyParams, ...event };
    _lazyParams.first = 0;
    _lazyParams.filters.address.value = searchValue;
    setLazyParams(_lazyParams);
  };

  const [dateError, setDateError] = useState(false);

  const setDateHandler = (e: Date) => {
    if (e === date) {
      return;
    }

    if (e === null) {
      setDateError(true);
    } else {
      setDateError(false);
      setDate(e);
    }
  };
  // ------------------------------------------------------------------------------

  return (
    <div>
      <div>
        <div className="p-grid  p-justify-between">
          <div className="p-mx-auto p-mt-3 p-pb-3">
            <CalendarWrapper
              className={!dateError ? "p-invalid" : ""}
              dateFormat="yy-mm-dd"
              id="basic"
              value={date}
              onChange={setDateHandler}
              maxDate={today}
              showIcon
              disabled={loading}
            />
          </div>
          <div className="p-mx-auto p-mt-3 p-pb-3">
            <SelectButton
              value={period}
              options={showPeriod}
              onChange={e => setPeriod(e.value == undefined ? period : e.value)}
              optionLabel="name"
              disabled={loading}
            />
          </div>
        </div>

        <div className="card">
          {measurementDevices.length !== 0 && filteredMeasurementDevices ? (
            <DataTable
              value={entries}
              paginator
              className="p-datatable-customers"
              rows={lazyParams.rows}
              first={lazyParams.first}
              totalRecords={filteredMeasurementDevices.length}
              dataKey="id"
              filters={lazyParams.filters}
              filterDisplay="row"
              responsiveLayout="scroll"
              onPage={onPage}
              onSort={onSort}
              onFilter={onFilter}
              globalFilterFields={[addressField]}
              emptyMessage="No records found."
            >
              {dynamicColumns}
            </DataTable>
          ) : (
            <div className="demo-container p-p-4">
              <ProgressSpinner className="p-d-block p-mx-auto" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
